import React, { useEffect, useState } from "react";
import Footer from "../../../core/common/footer/Footer";
import { useLocation, useNavigate } from "react-router";
import { all_routes } from "../../router/all_routes";
import Swal from "sweetalert2";
import { z } from "zod";
import useApi from "../../../core/customhook/useApi";
import { useSelector } from "react-redux";
import SnipperLoader from "../../../core/common/loader/SnipperLoader";
import { RootState } from "../../../core/data/redux/store";

const indianStates = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",

  // Union Territories
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

const normalizeStateName = (text: string) =>
  text
    .toLowerCase()
    .replace(/&/g, "and")
    .split(" ")
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
    .join(" ");

const toTitleCase = (text: string) =>
  text
    .toLowerCase()
    .split(" ")
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
    .join(" ");

const addressSchema = z.object({
  address_type: z.string().min(1, "Please select an address type"),
  pincode: z
    .string()
    .min(1, "Pincode is required")
    .regex(/^[1-9][0-9]{5}$/, "Enter a valid 6-digit Indian pincode"),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "Please select a state"),
  country: z.string().min(1, "Country is required"),
  address_line1: z.string().min(1, "Address line 1 is required"),
  address_line2: z.string().min(1, "Address line 2 is required"),
  address_line3: z.string().min(1, "Address line 3 is required"),
});

type AddressFormData = z.infer<typeof addressSchema>;
type AddressFormErrors = Partial<Record<keyof AddressFormData, string>>;

type AddressSourceData = {
  applicant_address?: string;
  aadhar_address?: string;
  applicant_address_pin_code?: number | string;
  aadhar_address_pin_code?: number | string;
};

type AddressSourceKey = "applicant_address" | "aadhar_address";

const AddressDetailCom = () => {
  const navigate = useNavigate();

  const reduxDashboardData = useSelector(
    (state: any) => state?.dashboard?.dashboardData,
  );

  const candidateId = reduxDashboardData?.assignmentDetails?.candidate_id;

  const getAddressAPI = useApi<any>(
    `/api/v1/candidates/addresses/${candidateId}`,
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );
  const getAddressTypeAPI = useApi<any>(
    `/api/v1/candidates/addresses/addresstype/${candidateId}`,
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const createAddressAPI = useApi<any>(
    `/api/v1/candidates/addresses/${candidateId}`,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const generateURLAPI = useApi<any>(
    `/api/v1/candidates/addresses/generate-address-url/${candidateId}`,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const [showNextStepModal, setShowNextStepModal] = useState(false);
  const [showWhatsappInput, setShowWhatsappInput] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState("");
  const [isCopied, setIsCopied] = useState(false);

  const [errors, setErrors] = useState<AddressFormErrors>({});
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyRemarks, setReplyRemarks] = useState("");
  const [replyDocuments, setReplyDocuments] = useState<File[]>([]);

  // Address sources (applicant / aadhar) coming from the API
  const [addressSource, setAddressSource] = useState<AddressSourceData | null>(
    null,
  );
  // Which source the user picked in the "Choose address" dropdown
  const [selectedAddressSource, setSelectedAddressSource] = useState<
    AddressSourceKey | ""
  >("");
  const [addressSourceError, setAddressSourceError] = useState("");

  // ---------- Get Api Data State ------------------------
  const [isEditMode, setIsEditMode] = useState(false);
  const [addressFetchStatus, setAddressFetchStatus] = useState<
    "loading" | "empty" | "existing" | "error"
  >("loading");

  const [formData, setFormData] = useState<AddressFormData>({
    address_type: "",
    pincode: "",
    city: "",
    state: "",
    country: "India",
    address_line1: "",
    address_line2: "",
    address_line3: "",
  });

  const HandleFetchAddress = async () => {
    try {
      const res = await getAddressTypeAPI.callApi();
      // API returns an array, the first item holds both addresses + pincodes
      setAddressSource(res?.data?.[0] || null);
    } catch (error) {
      console.log("error", error);
    }
  };

  useEffect(() => {
    if (candidateId) {
      HandleFetchAddress();
    }
  }, [candidateId]);

  const fetchDetails = async () => {
    setAddressFetchStatus("loading");
    try {
      const res = await getAddressAPI.callApi();
      if (!res?.success || !Array.isArray(res?.data)) {
        setAddressFetchStatus("error");
        return;
      }

      if (res.data.length === 0) {
        setIsEditMode(false);
        setAddressFetchStatus("empty");
        return;
      }

      const address = res.data[0];
      setIsEditMode(true);
      setAddressFetchStatus("existing");
      setFormData({
        address_type: address.address_type || "",
        pincode: String(address.pincode ?? ""),
        city: address.city || "",
        state: address.state || "",
        country: address.country || "India",
        address_line1: address.address_line1 || "",
        address_line2: address.address_line2 || "",
        address_line3: address.address_line3 || "",
      });
      setSelectedAddressSource(
        (address.address_name as AddressSourceKey) || "",
      );
    } catch (error) {
      console.error("Failed to fetch address details:", error);
      setAddressFetchStatus("error");
    }
  };

  useEffect(() => {
    if (candidateId) {
      fetchDetails();
    } else {
      setAddressFetchStatus("error");
    }
  }, [candidateId]);

  const handleContinueHere = () => {
    setShowNextStepModal(false);
    setShowWhatsappInput(false);
  };

  const handleFieldChange = (field: keyof AddressFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear that field's error as soon as the user starts correcting it.
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handlePincodeChange = async (value: string) => {
    const digitsOnly = value.replace(/\D/g, "").slice(0, 6);
    handleFieldChange("pincode", digitsOnly);

    if (digitsOnly.length !== 6) return;

    setPincodeLoading(true);

    try {
      const response = await fetch(
        `https://api.postalpincode.in/pincode/${digitsOnly}`,
      );
      const data = await response.json();

      const postOffice = data?.[0]?.PostOffice?.[0];

      if (data?.[0]?.Status === "Success" && postOffice) {
        const matchedState = indianStates.find(
          (s) =>
            s.toLowerCase() ===
            normalizeStateName(postOffice.State).toLowerCase(),
        );

        setFormData((prev) => ({
          ...prev,
          city: postOffice.District
            ? toTitleCase(postOffice.District)
            : prev.city,
          state: matchedState || prev.state,
          country: "India",
        }));

        setErrors((prev) => ({
          ...prev,
          city: undefined,
          state: undefined,
          country: undefined,
        }));
      } else {
        Swal.fire({
          icon: "warning",
          title: "Pincode Not Found",
          text: "We couldn't find details for this pincode. Please fill City and State manually.",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Lookup Failed",
        text: "Couldn't fetch location details. Please fill City and State manually.",
      });
    } finally {
      setPincodeLoading(false);
    }
  };

  // When user picks "Applicant address" / "Aadhar address", auto-fill the pincode
  // (this also triggers the same city/state lookup as typing the pincode manually)
  // const handleAddressSourceChange = (value: string) => {
  //   const key = value as AddressSourceKey | "";
  //   setSelectedAddressSource(key);

  //   if (!key || !addressSource) return;
  //   if (key) setAddressSourceError("");

  //   if (!key || !addressSource) return;

  //   const pin =
  //     key === "applicant_address"
  //       ? addressSource.applicant_address_pin_code
  //       : addressSource.aadhar_address_pin_code;

  //   if (pin) {
  //     handlePincodeChange(String(pin));
  //   }

  // };
  const handleAddressSourceChange = (value: string) => {
    const key = value as AddressSourceKey | "";
    setSelectedAddressSource(key);

    if (!key || !addressSource) return;

    setAddressSourceError("");

    const selectedAddress =
      key === "applicant_address"
        ? addressSource.applicant_address
        : addressSource.aadhar_address;

    const pin =
      key === "applicant_address"
        ? addressSource.applicant_address_pin_code
        : addressSource.aadhar_address_pin_code;

    if (selectedAddress) {
      handleFieldChange("address_line1", selectedAddress);
    }

    if (pin) {
      handlePincodeChange(String(pin));
    }
  };

  const handleCreate = async () => {
    if (!selectedAddressSource) {
      setAddressSourceError(
        "Please select the address where you're currently staying",
      );
      document
        .getElementById("address-source-section")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if (formData.address_type?.trim().length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Address Type Required",
        text: "Please select Address Type.",
        confirmButtonText: "Got it",
      });

      return;
    }

    try {
      const payload = {
        ...formData,
        address_name: selectedAddressSource, // "applicant_address" | "aadhar_address"
      };
      const response = await createAddressAPI.callApi(payload);

      if (response?.success) {
        setIsEditMode(true);
        setAddressFetchStatus("existing");
        setShowNextStepModal(true);
        setShowWhatsappInput(false);
        await fetchDetails();
      } else {
        Swal.fire({
          icon: "error",
          title: "Creation Failed",
          text: response?.error || response?.message || "Something went wrong",
        });
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Creation Failed",
        text:
          error?.response?.data?.error ||
          error?.message ||
          "Something went wrong",
      });
    }
  };

  const templates = useSelector((state: RootState) => state.package.templates);

  const aadharTemplate = templates.find(
    (item: any) => item.check_name === "Address Verification",
  );

  const assignmentTemplateId = aadharTemplate?.assignment_template_id;
  const caseId = aadharTemplate?.case_id;
  const assignmentId = aadharTemplate?.assignment_id;
  const { state } = useLocation();
  const {
    assignment_template_id,
    assignment_id,
    case_id,
    candidate_id,
    actor_type,
  } = state || {};

  const insuffResponse = useApi<any>(
    `/api/v1/insufficiency/candidate-response`,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  // Generate URL using API
  const handleGenerateURL = async () => {
    try {
      const response = await generateURLAPI.callApi({});

      if (response?.success && response?.data?.addressUrl) {
        setGeneratedUrl(response.data.addressUrl);
        setIsCopied(false);
        setShowWhatsappInput(true);
        setShowNextStepModal(true);
      } else {
        Swal.fire(
          "Error",
          response?.message || "Failed to generate URL",
          "error",
        );
      }
    } catch (error) {
      Swal.fire("Error", "Something went wrong", "error");
    }
  };

  // Copy generated URL
  const handleCopyURL = async () => {
    if (!generatedUrl) return;

    try {
      await navigator.clipboard.writeText(generatedUrl);

      setIsCopied(true);

      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch {
      Swal.fire({
        icon: "error",
        title: "Copy Failed",
        text: "Unable to copy the URL. Please copy it manually.",
      });
    }
  };

  // Open native system share dialog
  const handleShareURL = async () => {
    if (!generatedUrl) return;

    if (!navigator.share) {
      Swal.fire({
        icon: "info",
        title: "Sharing Not Supported",
        text: "Your browser does not support system sharing. Please use Copy URL instead.",
      });
      return;
    }

    try {
      await navigator.share({
        title: "Candidate Address Information",
        text: "Please use the following link to complete your address information:",
        url: generatedUrl,
      });
    } catch (error: any) {
      // User cancelled sharing
      if (error?.name !== "AbortError") {
        Swal.fire({
          icon: "error",
          title: "Sharing Failed",
          text: "Unable to share the generated URL.",
        });
      }
    }
  };

  // Return to initial modal screen
  const handleBackToOptions = () => {
    setShowWhatsappInput(false);
    setIsCopied(false);
    // Existing records have their own Next/Generate URL controls.
    if (addressFetchStatus === "existing") {
      setShowNextStepModal(false);
    }
  };

  const handleSubmitReply = async () => {
    const finalAssignmentTemplateId =
      assignment_template_id ?? assignmentTemplateId;

    const finalAssignmentId = assignment_id ?? assignmentId;

    const finalCaseId = case_id ?? caseId;

    const finalCandidateId = candidate_id ?? candidateId;

    if (!finalAssignmentTemplateId) {
      Swal.fire({
        icon: "warning",
        title: "Missing Assignment Template",
        text: "Assignment template information is missing.",
      });
      return;
    }

    if (!finalAssignmentId) {
      Swal.fire({
        icon: "warning",
        title: "Missing Assignment",
        text: "Assignment information is missing.",
      });
      return;
    }

    if (!finalCaseId) {
      Swal.fire({
        icon: "warning",
        title: "Missing Case",
        text: "Case information is missing.",
      });
      return;
    }

    if (!finalCandidateId) {
      Swal.fire({
        icon: "warning",
        title: "Missing Candidate",
        text: "Candidate information is missing.",
      });
      return;
    }

    if (!replyRemarks.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Remarks Required",
        text: "Please enter your remarks before submitting.",
      });
      return;
    }

    try {
      const payload = new FormData();

      payload.append(
        "assignment_template_id",
        String(finalAssignmentTemplateId),
      );

      payload.append("assignment_id", String(finalAssignmentId));

      payload.append("case_id", String(finalCaseId));

      payload.append("candidate_id", String(finalCandidateId));

      payload.append("actor_due_on", actor_type || "CANDIDATE");

      payload.append("remarks", replyRemarks.trim());

      replyDocuments.forEach((file) => {
        payload.append("documents", file);
      });

      const res = await insuffResponse.callApi(payload);

      if (res?.success) {
        await Swal.fire({
          icon: "success",
          title: "Success",
          text: "Your response has been submitted successfully.",
        });

        setReplyRemarks("");
        setReplyDocuments([]);
        setShowReplyModal(false);

        navigate(all_routes.caseSummary);
      } else {
        Swal.fire({
          icon: "error",
          title: "Submission Failed",
          text: res?.error || res?.message || "Unable to submit your response.",
        });
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text:
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          "Unable to submit your response.",
      });
    }
  };

  const removeReplyFile = (index: number) => {
    setReplyDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleReplyFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    setReplyDocuments(Array.from(e.target.files));
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {addressFetchStatus === "loading" ? (
            <SnipperLoader />
          ) : (
            <div className="card p-4 overflow-hidden">
              <div className="border-bottom pb-3 mb-4 d-flex flex-wrap align-items-center justify-content-between gap-3">
                <h5 className="fw-semibold mb-0">Candidate Address Details</h5>
                <div className="d-flex flex-wrap gap-2">
                  {isEditMode && (
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={handleGenerateURL}
                      disabled={generateURLAPI.loading}
                    >
                      {generateURLAPI.loading
                        ? "Generating URL..."
                        : "Generate URL"}
                    </button>
                  )}
                </div>
              </div>

              <div className="card-body p-0">
                <style>{`
                  .addr-card {
                    position: relative;
                    display: block;
                    height: 100%;
                    padding: 14px;
                    border: 1.5px solid #E5E7EB;
                    border-radius: 16px;
                    background: #fff;
                    cursor: pointer;
                    transition: border-color .2s ease, box-shadow .2s ease, background .2s ease, transform .2s ease;
                  }
                  .addr-card:hover {
                    border-color: #F4B183;
                    box-shadow: 0 8px 24px rgba(230, 126, 34, 0.12);
                    transform: translateY(-2px);
                  }
                  .addr-card.selected {
                    border-color: #E67E22;
                    background: linear-gradient(135deg, #FFF8F0 0%, #FFFFFF 100%);
                    box-shadow: 0 8px 24px rgba(230, 126, 34, 0.18);
                  }
                  .addr-card input[type="radio"] {
                    position: absolute;
                    opacity: 0;
                    pointer-events: none;
                  }
                  .addr-card input[type="radio"]:focus-visible ~ .addr-radio {
                    outline: 3px solid rgba(230, 126, 34, 0.35);
                    outline-offset: 2px;
                  }
                  .addr-icon {
                    width: 30px;
                    height: 30px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    background: #F3F4F6;
                    color: #6B7280;
                    font-size: 18px;
                    transition: background .2s ease, color .2s ease;
                  }
                  .addr-card.selected .addr-icon {
                    background: #E67E22;
                    color: #fff;
                  }
                  .addr-radio {
                    width: 20px;
                    height: 20px;
                    border-radius: 50%;
                    border: 2px solid #D1D5DB;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    background: #fff;
                    transition: all .2s ease;
                  }
                  .addr-card.selected .addr-radio {
                    border-color: #E67E22;
                    background: #E67E22;
                  }
                  .addr-radio i {
                    color: #fff;
                    font-size: 14px;
                    opacity: 0;
                    transition: opacity .2s ease;
                  }
                  .addr-card.selected .addr-radio i {
                    opacity: 1;
                  }
                    .addr-card.addr-card-error {
  border-color: #DC3545;
  background: #FFF5F5;
}
                  .addr-text {
                    background: #F9FAFB;
                    border-radius: 8px;
                    padding: 6px 12px;
                    color: #374151;
                    line-height: 1.55;
                    font-size: 12px;
                  }
                  .addr-card.selected .addr-text {
                    background: #FFF3E6;
                  }
                  .addr-pin {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 4px 12px;
                    border-radius: 8px;
                    font-size: 12px;
                    font-weight: 600;
                    background: #F3F4F6;
                    color: #4B5563;
                    transition: background .2s ease, color .2s ease;
                  }
                  .addr-card.selected .addr-pin {
                    background: #FFE3C7;
                    color: #B45309;
                  }
                `}</style>

                <div className="mb-4" id="address-source-section">
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <i
                      className="ti ti-map-pin-star fs-3"
                      style={{ color: "#E67E22" }}
                    ></i>
                    <h6 className="fw-bold mb-0">
                      Select the address where you're currently staying
                      {!isEditMode && (
                        <span className="text-danger ms-1">*</span>
                      )}
                    </h6>
                  </div>
                  <p className="text-muted small mb-3">
                    Pick one of your saved addresses. Its pincode, city and
                    state will be filled in for you below.
                  </p>

                  {getAddressTypeAPI.loading && (
                    <div className="d-flex align-items-center gap-2 text-muted small">
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        style={{ color: "#E67E22" }}
                      />
                      Loading your addresses...
                    </div>
                  )}

                  <div
                    className="row g-3"
                    role="radiogroup"
                    aria-label="Choose address"
                  >
                    {(
                      [
                        {
                          key: "applicant_address" as AddressSourceKey,
                          title: "Applicant Address",
                          subtitle: "Address given by you",
                          icon: "ti ti-home-2",
                          address: addressSource?.applicant_address,
                          pin: addressSource?.applicant_address_pin_code,
                        },
                        {
                          key: "aadhar_address" as AddressSourceKey,
                          title: "Aadhar Address",
                          subtitle: "Address on your Aadhar card",
                          icon: "ti ti-id",
                          address: addressSource?.aadhar_address,
                          pin: addressSource?.aadhar_address_pin_code,
                        },
                      ] as const
                    )
                      .filter((item) => item.address)
                      .map((item) => {
                        const isSelected = selectedAddressSource === item.key;

                        return (
                          <div className="col-md-6" key={item.key}>
                            <label
                              className={`addr-card ${isSelected ? "selected" : ""} ${
                                addressSourceError && !isSelected
                                  ? "addr-card-error"
                                  : ""
                              }`}
                            >
                              <input
                                type="radio"
                                name="addressSource"
                                value={item.key}
                                checked={isSelected}
                                onChange={(e) =>
                                  handleAddressSourceChange(e.target.value)
                                }
                              />

                              {/* Header */}
                              <div className="d-flex align-items-center gap-3 mb-1">
                                <div className="addr-icon">
                                  <i className={item.icon}></i>
                                </div>

                                <div className="flex-grow-1">
                                  <div className="fw-bold text-dark">
                                    {item.title}
                                  </div>
                                  <div className="text-muted small">
                                    {item.subtitle}
                                  </div>
                                </div>

                                <span className="addr-radio">
                                  <i className="ti ti-check"></i>
                                </span>
                              </div>

                              {/* Full address */}
                              <div className="addr-text d-flex gap-2 mb-1">
                                <i
                                  className="ti ti-map-pin flex-shrink-0 mt-1"
                                  style={{
                                    color: isSelected ? "#E67E22" : "#9CA3AF",
                                  }}
                                ></i>
                                <span>{item.address}</span>
                              </div>

                              {/* Pincode */}
                              {item.pin && (
                                <span className="addr-pin">
                                  <i className="ti ti-mailbox"></i>
                                  Pincode: {item.pin}
                                </span>
                              )}
                            </label>
                          </div>
                        );
                      })}
                  </div>

                  {addressSourceError && (
                    <div className="text-danger small mt-2 d-flex align-items-center gap-1">
                      <i className="ti ti-alert-circle"></i>
                      {addressSourceError}
                    </div>
                  )}

                  {selectedAddressSource && (
                    <div
                      className="d-flex align-items-center gap-2 mt-3 small"
                      style={{ color: "#B45309" }}
                    >
                      <i className="ti ti-circle-check fs-3"></i>
                      Pincode has been filled from your selected address.
                    </div>
                  )}
                </div>
                <div className="row g-3">
                  {/* Address Type */}
                  <div className="col-12 col-md-6 col-lg-4">
                    <label className="form-label fw-medium">
                      Address Type <span className="text-danger ms-1">*</span>
                    </label>

                    <select
                      className={`form-select ${
                        errors.address_type ? "is-invalid" : ""
                      }`}
                      value={formData.address_type}
                      onChange={(e) =>
                        handleFieldChange("address_type", e.target.value)
                      }
                      disabled={isEditMode}
                    >
                      <option value="">Select Address Type</option>
                      <option value="Permanent">Permanent</option>
                      <option value="Current">Current</option>
                    </select>
                    {errors.address_type && (
                      <div className="invalid-feedback">
                        {errors.address_type}
                      </div>
                    )}
                  </div>
                  {/* Pincode */}
                  <div className="col-12 col-md-6 col-lg-4">
                    <label className="form-label fw-medium">
                      Pincode
                      <span className="text-danger ms-1">*</span>
                    </label>

                    <div className="position-relative">
                      <input
                        type="text"
                        className={`form-control pe-5 ${
                          errors.pincode ? "is-invalid" : ""
                        }`}
                        placeholder="Enter Pincode"
                        value={formData.pincode || ""}
                        maxLength={6}
                        inputMode="numeric"
                        autoComplete="postal-code"
                        onChange={(e) => handlePincodeChange(e.target.value)}
                        disabled
                      />

                      {pincodeLoading && (
                        <span
                          className="spinner-border spinner-border-sm position-absolute"
                          style={{
                            right: errors.pincode ? "38px" : "14px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: "#f08934",
                          }}
                          role="status"
                          aria-hidden="true"
                        />
                      )}
                    </div>

                    {errors.pincode && (
                      <div className="invalid-feedback d-block">
                        {errors.pincode}
                      </div>
                    )}
                  </div>

                  {/* City */}
                  <div className="col-12 col-md-6 col-lg-4">
                    <label className="form-label fw-medium">
                      City
                      <span className="text-danger ms-1">*</span>
                    </label>

                    <input
                      type="text"
                      className={`form-control ${
                        errors.city ? "is-invalid" : ""
                      }`}
                      placeholder="Enter City"
                      value={formData.city || ""}
                      autoComplete="address-level2"
                      onChange={(e) =>
                        handleFieldChange("city", e.target.value)
                      }
                      disabled
                    />

                    {errors.city && (
                      <div className="invalid-feedback">{errors.city}</div>
                    )}
                  </div>

                  {/* State */}
                  <div className="col-12 col-md-6 col-lg-4">
                    <label className="form-label fw-medium">
                      State
                      <span className="text-danger ms-1">*</span>
                    </label>

                    <select
                      className={`form-select ${
                        errors.state ? "is-invalid" : ""
                      }`}
                      value={formData.state || ""}
                      autoComplete="address-level1"
                      onChange={(e) =>
                        handleFieldChange("state", e.target.value)
                      }
                      disabled
                    >
                      <option value="">Select State</option>

                      {indianStates.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>

                    {errors.state && (
                      <div className="invalid-feedback">{errors.state}</div>
                    )}
                  </div>

                  {/* Country */}
                  <div className="col-12 col-md-6 col-lg-4">
                    <label className="form-label fw-medium">
                      Country
                      <span className="text-danger ms-1">*</span>
                    </label>

                    <select
                      className={`form-select ${
                        errors.country ? "is-invalid" : ""
                      }`}
                      value={formData.country || "India"}
                      autoComplete="country-name"
                      onChange={(e) =>
                        handleFieldChange("country", e.target.value)
                      }
                      disabled
                    >
                      <option value="India">India</option>
                    </select>

                    {errors.country && (
                      <div className="invalid-feedback">{errors.country}</div>
                    )}
                  </div>

                  {/* Address */}
                  <div className="col-12">
                    <label className="form-label fw-medium">
                      Address
                      <span className="text-danger ms-1">*</span>
                    </label>

                    <input
                      type="text"
                      className={`form-control ${
                        errors.address_line1 ? "is-invalid" : ""
                      }`}
                      placeholder="Flat / House No. / Building / Street / Locality"
                      value={formData.address_line1 || ""}
                      autoComplete="address-line1"
                      onChange={(e) =>
                        handleFieldChange("address_line1", e.target.value)
                      }
                      disabled
                    />

                    {errors.address_line1 && (
                      <div className="invalid-feedback">
                        {errors.address_line1}
                      </div>
                    )}
                  </div>
                </div>

                <div
                  className="my-4 p-3"
                  style={{
                    background:
                      "linear-gradient(135deg, #FFF8F0 0%, #FFFCF8 100%)",
                    border: "1px solid #FFD8B5",
                    borderRadius: "12px",
                    boxShadow: "0 3px 10px rgba(230, 126, 34, 0.08)",
                  }}
                >
                  <div className="d-flex align-items-start gap-3">
                    {/* Icon */}
                    <div
                      className="d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: "44px",
                        height: "44px",
                        borderRadius: "10px",
                        background: "#E67E22",
                      }}
                    >
                      <i className="ti ti-map-pin-check text-white fs-3"></i>
                    </div>

                    {/* Content */}
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <h6 className="mb-0 fw-bold text-dark">
                          Location Verification Required
                        </h6>

                        <span
                          className="badge rounded-pill"
                          style={{
                            background: "#FFF3E0",
                            color: "#D97706",
                            border: "1px solid #FFD8A8",
                          }}
                        >
                          Important
                        </span>
                      </div>

                      <p className="mb-2 text-secondary">
                        Please make sure you are physically available at the
                        address you have provided.
                      </p>

                      <div
                        className="d-flex align-items-center gap-2 p-2 rounded-2"
                        style={{
                          background: "#FFFFFF",
                          border: "1px dashed #F4B183",
                        }}
                      >
                        <i
                          className="ti ti-location"
                          style={{
                            color: "#E67E22",
                          }}
                        ></i>

                        <span className="text-dark">
                          Your current location must be within{" "}
                          <strong style={{ color: "#D97706" }}>
                            1 kilometers
                          </strong>{" "}
                          of the submitted address.
                        </span>
                      </div>

                      <div className="mt-2 d-flex align-items-center gap-1">
                        <i
                          className="ti ti-alert-circle fs-3"
                          style={{
                            color: "#E67E22",
                          }}
                        ></i>

                        <p className="text-muted mb-0">
                          If the distance is greater than{" "}
                          <strong style={{ color: "#D97706" }}>
                            1 kilometers
                          </strong>
                          , it may create a discrepancy at the time of
                          verification.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-3 mt-4">
                  {!isEditMode ? (
                    <button className="btn btn-primary" onClick={handleCreate}>
                      Save & Next
                    </button>
                  ) : (
                    <button
                      className="btn btn-primary"
                      onClick={() => navigate(all_routes.locationInfo)}
                    >
                      Next
                    </button>
                    // <button className="btn btn-primary" onClick={handleCreate}>
                    //   Save & Next
                    // </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {showNextStepModal && (
            <div
              className="modal fade show d-block"
              tabIndex={-1}
              role="dialog"
              aria-modal="true"
              style={{ background: "rgba(0, 0, 0, 0.5)" }}
            >
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content border-0 rounded-4 shadow-lg overflow-hidden">
                  {/* HEADER */}
                  <div className="modal-header border-0 pb-0 px-4 pt-4 d-flex align-items-center justify-content-between">
                    <h4 className="fw-bold mb-0">
                      {showWhatsappInput
                        ? "Generated Address URL"
                        : "Choose Next Step"}
                    </h4>

                    <button
                      type="button"
                      className="btn-close shadow-none"
                      aria-label="Close"
                      onClick={() => {
                        setShowNextStepModal(false);
                        setShowWhatsappInput(false);
                      }}
                    />
                  </div>

                  {/* BODY */}
                  <div className="modal-body p-4">
                    {!showWhatsappInput ? (
                      <div className="slide-left">
                        <p className="text-center text-muted fw-medium mb-4">
                          {addressFetchStatus === "empty"
                            ? "Choose how you want to provide your address details."
                            : "Where would you like to proceed for the next step?"}
                        </p>

                        {/* CONTINUE HERE - UNCHANGED */}
                        <button
                          type="button"
                          className="btn btn-primary w-100 py-3 mb-3 rounded-3 fw-medium"
                          onClick={() => {
                            if (addressFetchStatus === "existing") {
                              navigate(all_routes.locationInfo);
                            } else {
                              handleContinueHere();
                            }
                          }}
                        >
                          Continue Here
                          <i className="bi bi-arrow-right ms-2"></i>
                        </button>

                        {/* GENERATE URL */}
                        <button
                          type="button"
                          className="btn btn-success w-100 py-3 rounded-3 fw-medium"
                          onClick={handleGenerateURL}
                          disabled={generateURLAPI?.loading}
                        >
                          {generateURLAPI?.loading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              />
                              Generating URL...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-link-45deg me-2"></i>
                              Generate URL
                            </>
                          )}
                        </button>
                      </div>
                    ) : (
                      <div className="slide-right">
                        {/* SUCCESS SECTION */}
                        <div className="text-center mb-4">
                          <div
                            className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                            style={{
                              width: 64,
                              height: 64,
                              backgroundColor: "#e8f7ee",
                            }}
                          >
                            <i
                              className="bi bi-check-circle-fill text-success"
                              style={{ fontSize: 32 }}
                            />
                          </div>

                          <h5 className="fw-bold text-dark mb-2">
                            URL Generated Successfully!
                          </h5>

                          <p
                            className="text-muted small mx-auto mb-0"
                            style={{ maxWidth: 340, lineHeight: 1.6 }}
                          >
                            Your address verification link is ready. Copy or
                            share it with the candidate to proceed.
                          </p>
                        </div>

                        {/* GENERATED LINK SECTION */}
                        <div
                          className="rounded-3 p-3 mb-4"
                          style={{
                            backgroundColor: "#f8f9fc",
                            border: "1px solid #e8ecf1",
                          }}
                        >
                          <label
                            htmlFor="generatedAddressUrl"
                            className="form-label fw-semibold text-dark small mb-2"
                          >
                            <i className="bi bi-link-45deg me-2 text-primary" />
                            Generated Address URL
                          </label>

                          <div className="input-group">
                            <input
                              id="generatedAddressUrl"
                              type="text"
                              className="form-control bg-white border-end-0"
                              value={generatedUrl}
                              readOnly
                              onFocus={(e) => e.target.select()}
                              aria-label="Generated address URL"
                              style={{
                                height: 44,
                                fontSize: 13,
                                textOverflow: "ellipsis",
                              }}
                            />

                            <span className="input-group-text bg-white border-start-0">
                              <i className="bi bi-link-45deg text-muted" />
                            </span>
                          </div>

                          <div className="d-flex align-items-center gap-2 mt-2">
                            <i className="bi bi-info-circle text-muted small" />
                            <small className="text-muted">
                              Use the buttons below to copy or share this link.
                            </small>
                          </div>
                        </div>

                        {/* COPY & SHARE BUTTONS - SAME ROW */}
                        <div className="d-flex gap-3 mb-3">
                          <button
                            type="button"
                            className={`btn ${
                              isCopied ? "btn-success" : "btn-outline-primary"
                            } d-flex align-items-center justify-content-center gap-2 fw-semibold rounded-3 flex-fill`}
                            style={{ minWidth: 0, height: 46 }}
                            onClick={handleCopyURL}
                          >
                            <i
                              className={`bi ${
                                isCopied ? "bi-check-lg" : "bi-clipboard"
                              }`}
                            />
                            <span>{isCopied ? "Copied!" : "Copy URL"}</span>
                          </button>

                          <button
                            type="button"
                            className="btn btn-success d-flex align-items-center justify-content-center gap-2 fw-semibold rounded-3 flex-fill"
                            style={{ minWidth: 0, height: 46 }}
                            onClick={handleShareURL}
                          >
                            <i className="bi bi-share" />
                            <span>Share URL</span>
                          </button>
                        </div>

                        {/* BACK BUTTON - SEPARATE ROW BELOW */}
                        <div className="pt-3 border-top">
                          <button
                            type="button"
                            className="btn btn-outline-secondary d-inline-flex align-items-center justify-content-center gap-2 rounded-3 px-4 w-100"
                            style={{ height: 40 }}
                            onClick={handleBackToOptions}
                          >
                            <i className="bi bi-arrow-left" />
                            Back
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {showReplyModal && (
            <div
              className="modal fade show d-block"
              style={{ background: "rgba(0,0,0,.5)" }}
            >
              <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content border-0 shadow">
                  {/* Header */}
                  <div className="modal-header">
                    <div>
                      <h5 className="modal-title fw-bold mb-1">
                        Respond to Insufficiency
                      </h5>

                      <small className="text-muted">
                        Please provide your explanation and upload supporting
                        documents if applicable.
                      </small>
                    </div>

                    <button
                      className="btn-close"
                      onClick={() => setShowReplyModal(false)}
                    />
                  </div>

                  {/* Body */}
                  <div className="modal-body">
                    {/* Remarks */}
                    <div className="mb-4">
                      <label className="form-label fw-semibold">
                        Remarks
                        <span className="text-danger ms-1">*</span>
                      </label>

                      <textarea
                        rows={5}
                        className="form-control"
                        placeholder="Enter your response to the verifier..."
                        value={replyRemarks}
                        onChange={(e) => setReplyRemarks(e.target.value)}
                        style={{ resize: "none" }}
                      />

                      <small className="text-muted">
                        Clearly explain the requested information or
                        clarification.
                      </small>
                    </div>

                    {/* Upload */}
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Supporting Documents
                      </label>

                      <input
                        type="file"
                        className="form-control"
                        multiple
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={handleReplyFileChange}
                      />

                      <small className="text-muted">
                        Accepted formats: JPG, JPEG, PNG, PDF (Maximum 10 MB per
                        file)
                      </small>
                    </div>

                    {/* Selected Files */}
                    {replyDocuments.length > 0 && (
                      <div className="mt-3">
                        <label className="fw-semibold mb-2">
                          Selected Files
                        </label>

                        <div className="d-flex flex-wrap gap-2">
                          {replyDocuments.map((file, index) => (
                            <span
                              key={index}
                              className="badge rounded-pill bg-success-subtle text-dark border border-success-subtle px-3 py-2 d-flex align-items-center"
                            >
                              <i className="ti ti-paperclip me-2"></i>

                              <span
                                className="text-truncate"
                                style={{ maxWidth: 180 }}
                              >
                                {file.name}
                              </span>

                              <button
                                type="button"
                                className="btn btn-sm border-0 bg-transparent text-danger ms-2 p-0"
                                onClick={() => removeReplyFile(index)}
                              >
                                <i className="ti ti-x"></i>
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="modal-footer gap-3">
                    <button
                      className="btn btn-light border"
                      onClick={() => setShowReplyModal(false)}
                    >
                      Cancel
                    </button>

                    <button
                      className="btn btn-primary"
                      onClick={handleSubmitReply}
                      disabled={insuffResponse.loading}
                    >
                      {insuffResponse.loading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <i className="ti ti-send me-2"></i>
                          Submit Response
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <Footer />
      </div>
    </>
  );
};

export default AddressDetailCom;
